// AnimalAddendumFormPage — /animal-addendum/:token. PUBLIC, tenant-facing.
//
// WEL-153. A current tenant told us they are getting an animal; every resident
// and co-signer on the tenancy got their own tokenized link here. The form asks
// for the NEW animal(s) — what kind of animal it is, then only that kind's
// questions (WEL-173, lib/animalQuestions.ts: a dog's breed, weight and age; a
// cat's age and color; a hamster's species…), and an optional photo for a dog,
// cat or bird — and shows the animals we already have on file read-only, so
// nobody re-enters a cat we already know about. The FIRST completion wins and
// locks; a later opener is told so.
//
// Like MoveoutFundsIntakeFormPage this is a standalone branded surface (NOT
// inside the authenticated AppShell — see App.tsx) and talks ONLY to the public,
// token-gated /api/animal-addendum/intake/:token endpoints (no Bearer).
//
// Copy notes (docs/bespoke-communications-doctrine.md). The reader is a current
// tenant, so:
//   • the address is DEMOTED to the lockup — they know where they live;
//   • the document noun is "addendum", never "lease" (their lease is not
//     changing hands, an addendum is being added to it);
//   • the rent/deposit sentence comes from the server's `terms` so this page,
//     the emails and the document all say the same numbers the same way;
//   • no manufactured warmth, and no promise the addendum does not make.
// Nothing here ever prints an id (docs/no-ids-in-ui.md) — the animal ids exist
// only to attach a photo to the right row.

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  AnimalAddendumApiError,
  animalAddendumReasons,
  animalNamesSentence,
  buildAnimalAddendumSubmission,
  canSubmitAnimalAddendum,
  getAnimalAddendumIntake,
  newAnimalAddendumAnimal,
  newAnimalAddendumDraft,
  photoUploadMessage,
  removeAnimalFromDraft,
  submitAnimalAddendumIntake,
  uploadAnimalAddendumPhoto,
  type AnimalAddendumDraftAnimal,
  type AnimalAddendumIntakeView,
  type AnimalAddendumKnownAnimal,
} from '@/lib/animalAddendumClient';
import { AnimalAddendumAnimalCard } from '@/components/AnimalAddendumAnimalCard';
import { AnimalAddendumTerms } from './AnimalAddendumTerms';

// ---------------------------------------------------------------------------
// Brand chrome — the shared lockup + section card the other public forms use.
// ---------------------------------------------------------------------------

function BrandLockup({ propertyAddress }: { propertyAddress: string | null }): React.JSX.Element {
  return (
    <header style={{ textAlign: 'center', marginBottom: 'var(--space-7)' }}>
      <div
        style={{
          fontFamily: 'var(--font-serif)',
          fontSize: 26,
          lineHeight: 1.2,
          letterSpacing: '0.16em',
          textTransform: 'uppercase',
          color: 'var(--fg)',
        }}
      >
        Welcome&nbsp;Home
      </div>
      <div
        style={{
          fontFamily: 'var(--font-serif)',
          fontSize: 13,
          lineHeight: 1.2,
          letterSpacing: '0.42em',
          textTransform: 'uppercase',
          color: 'var(--accent)',
          paddingLeft: '0.42em',
        }}
      >
        Properties
      </div>
      <div
        style={{
          width: 56,
          height: 1,
          backgroundColor: 'var(--accent)',
          margin: 'var(--space-4) auto 0',
        }}
      />
      {propertyAddress ? (
        <p
          style={{
            marginTop: 'var(--space-4)',
            fontFamily: 'var(--font-sans)',
            fontSize: 'var(--text-md)',
            color: 'var(--fg-muted)',
          }}
        >
          {propertyAddress}
        </p>
      ) : null}
    </header>
  );
}

function SectionCard(props: {
  title: string;
  description?: string;
  children: React.ReactNode;
}): React.JSX.Element {
  return (
    <section className="card" style={{ marginBottom: 'var(--space-5)' }}>
      <div style={{ marginBottom: 'var(--space-5)' }}>
        <h2
          style={{
            fontFamily: 'var(--font-serif)',
            fontSize: 'var(--text-lg)',
            fontWeight: 400,
            color: 'var(--fg)',
            margin: 0,
          }}
        >
          {props.title}
        </h2>
        {props.description ? (
          <p className="modal-description" style={{ marginTop: 'var(--space-2)' }}>
            {props.description}
          </p>
        ) : null}
      </div>
      {props.children}
    </section>
  );
}

/** One animal AppFolio already has, as a read-only line. Only what we KNOW. */
function knownAnimalLine(a: AnimalAddendumKnownAnimal): string {
  const bits = [a.typeLabel, a.age ? `${a.age} years old` : '', a.weightLbs ? `${a.weightLbs} lb` : '']
    .map((b) => b.trim())
    .filter(Boolean);
  return bits.length > 0 ? `${a.name} — ${bits.join(' · ')}` : a.name;
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export function AnimalAddendumFormPage(): React.JSX.Element {
  const { token = '' } = useParams<{ token: string }>();

  const [view, setView] = useState<AnimalAddendumIntakeView | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  /** Set when THIS person's submission won — drives the thank-you screen. */
  const [justSubmitted, setJustSubmitted] = useState(false);

  const [animals, setAnimals] = useState<AnimalAddendumDraftAnimal[]>(
    () => newAnimalAddendumDraft().animals,
  );
  /** animalId → in-flight upload / last upload error (per animal, not global). */
  const [uploading, setUploading] = useState<Record<string, boolean>>({});
  const [photoErrors, setPhotoErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setLoadFailed(false);
    getAnimalAddendumIntake(token)
      .then((v) => {
        if (!alive) return;
        setView(v);
        // Covered animals are the form's first rows (name prefilled, not removable); an ordinary round
        // keeps the one blank animal it opened with.
        if (v.coveredAnimals.length > 0) setAnimals(newAnimalAddendumDraft(v.coveredAnimals).animals);
      })
      .catch(() => {
        // Any failure reads the same to the tenant: the link is not working.
        // Which failure it was is our problem, not theirs.
        if (alive) setLoadFailed(true);
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [token]);

  const draft = useMemo(() => ({ animals }), [animals]);
  const reasons = useMemo(() => animalAddendumReasons(draft), [draft]);
  const canSubmit = useMemo(() => canSubmitAnimalAddendum(draft), [draft]);

  const updateAnimal = useCallback((id: string, patch: Partial<AnimalAddendumDraftAnimal>) => {
    setAnimals((prev) => prev.map((a) => (a.id === id ? { ...a, ...patch } : a)));
  }, []);

  const removeAnimal = useCallback((id: string) => {
    setAnimals((prev) => removeAnimalFromDraft(prev, id));
  }, []);

  const addAnimal = useCallback(() => {
    setAnimals((prev) => [...prev, newAnimalAddendumAnimal()]);
  }, []);

  const clearPhoto = useCallback((id: string) => {
    // Client-side only: the stored photo is keyed by animal id and is simply
    // ignored server-side if this animal ends up without one.
    setAnimals((prev) =>
      prev.map((a) => {
        if (a.id !== id) return a;
        const { photo: _dropped, ...rest } = a;
        return rest;
      }),
    );
    setPhotoErrors((prev) => {
      const { [id]: _gone, ...rest } = prev;
      return rest;
    });
  }, []);

  const pickPhoto = useCallback(
    async (id: string, file: File) => {
      setUploading((prev) => ({ ...prev, [id]: true }));
      setPhotoErrors((prev) => {
        const { [id]: _gone, ...rest } = prev;
        return rest;
      });
      try {
        const res = await uploadAnimalAddendumPhoto(token, id, file);
        setAnimals((prev) =>
          prev.map((a) =>
            a.id === id
              ? {
                  ...a,
                  photo: {
                    id: res.photo.id,
                    contentType: res.photo.contentType,
                    bytes: res.photo.bytes,
                    previewUrl: res.photo.previewUrl,
                  },
                }
              : a,
          ),
        );
      } catch (err) {
        // Never blocks the submit — the document just prints the "email us a
        // photo" line for this animal instead.
        setPhotoErrors((prev) => ({ ...prev, [id]: photoUploadMessage(err) }));
      } finally {
        setUploading((prev) => ({ ...prev, [id]: false }));
      }
    },
    [token],
  );

  const handleSubmit = useCallback(async () => {
    const body = buildAnimalAddendumSubmission({ animals });
    if (!body) return;
    setSaving(true);
    setSaveError(null);
    try {
      const res = await submitAnimalAddendumIntake(token, body);
      if (res.alreadyLocked) {
        // Somebody in the household got there first while this form was open.
        setView((prev) =>
          prev ? { ...prev, state: 'locked', alreadyLocked: true } : prev,
        );
      } else {
        setJustSubmitted(true);
      }
    } catch (err) {
      setSaveError(
        err instanceof AnimalAddendumApiError
          ? ((err.body as { error?: string } | null)?.error ??
              'We couldn’t save that. Please try again.')
          : 'We couldn’t save that. Please try again.',
      );
    } finally {
      setSaving(false);
    }
  }, [animals, token]);

  const shell = useCallback(
    (children: React.ReactNode): React.JSX.Element => (
      <div
        style={{
          minHeight: '100vh',
          backgroundColor: 'var(--bg-alt)',
          padding: 'var(--space-7) var(--space-4)',
        }}
      >
        <div style={{ maxWidth: 720, margin: '0 auto' }}>{children}</div>
      </div>
    ),
    [],
  );

  if (loading) {
    return shell(
      <>
        <BrandLockup propertyAddress={null} />
        <p style={{ textAlign: 'center', color: 'var(--fg-muted)' }}>Loading…</p>
      </>,
    );
  }

  // A failed load and an unavailable link are the SAME neutral state: the link
  // never says whether it was wrong, expired, or withdrawn.
  if (loadFailed || !view || view.state === 'unavailable') {
    return shell(
      <>
        <BrandLockup propertyAddress={view?.premises || null} />
        <SectionCard
          title="This link is no longer active"
          description="There is nothing to fill out here. If we still need something from you, we'll send a fresh link — or just reply to our email and a real person will help."
        >
          <></>
        </SectionCard>
      </>,
    );
  }

  const premises = view.premises || null;

  // This person just completed it — the addendum is already on its way out.
  if (justSubmitted) {
    const named = animalNamesSentence(animals.map((a) => a.name));
    return shell(
      <>
        <BrandLockup propertyAddress={premises} />
        <div className="card" data-tone="success" style={{ textAlign: 'center' }}>
          <h1
            style={{
              fontFamily: 'var(--font-serif)',
              fontSize: 28,
              fontWeight: 400,
              margin: 0,
              color: 'var(--fg)',
            }}
          >
            Thank you — that&apos;s everything we needed.
          </h1>
          <p style={{ marginTop: 'var(--space-4)', color: 'var(--fg-muted)' }}>
            The addendum {named ? <>adding {named} </> : null}is on its way to everyone in your
            household to sign. Once it&apos;s signed, we&apos;ll email you a copy.
          </p>
        </div>
      </>,
    );
  }

  // Somebody else in the household completed it first.
  if (view.state === 'locked' || view.state === 'sent') {
    const first = view.lockedByName?.trim().split(/\s+/)[0] ?? '';
    return shell(
      <>
        <BrandLockup propertyAddress={premises} />
        <SectionCard
          title="Already taken care of"
          description={
            first
              ? `${first} already told us about your new animal, so there's nothing more for you to do here.`
              : "Someone in your household already told us about your new animal, so there's nothing more for you to do here."
          }
        >
          <p style={{ color: 'var(--fg-muted)', margin: 0 }}>
            The addendum goes to everyone in your household to sign — watch for that email. If
            something in it doesn&apos;t look right, reply to our email and we&apos;ll sort it out.
          </p>
        </SectionCard>
      </>,
    );
  }

  // --- Fillable ---
  const greeting = view.recipient?.name ? `, ${view.recipient.name.split(/\s+/)[0]}` : '';
  const terms = view.terms;
  const householdNames = view.household.map((h) => h.name).filter(Boolean);

  return shell(
    <>
      <BrandLockup propertyAddress={premises} />
      <div style={{ marginBottom: 'var(--space-6)', textAlign: 'center' }}>
        <h1
          style={{
            fontFamily: 'var(--font-serif)',
            fontSize: 26,
            fontWeight: 400,
            margin: 0,
            color: 'var(--fg)',
          }}
        >
          Tell us about your new animal{greeting}
        </h1>
        <p style={{ marginTop: 'var(--space-3)', color: 'var(--fg-muted)' }}>
          A few details and we&apos;ll put the addendum together for everyone to sign.
        </p>
      </div>

      {/* First-completion-wins, before the inputs — the same rule the other
          household forms carry, worded for this one. */}
      <div className="alert" data-tone="info" style={{ marginBottom: 'var(--space-5)' }}>
        <div className="alert-body">
          <div className="alert-title">Any one of you can fill this in</div>
          <p style={{ margin: 'var(--space-2) 0 0', fontSize: 'var(--text-sm)' }}>
            {householdNames.length > 1
              ? `Everyone on the lease got this link (${householdNames.join(', ')}). `
              : 'Everyone on the lease got this link. '}
            <strong>The first answers we receive are the ones we use</strong>, so check with the
            others before you submit.
          </p>
        </div>
      </div>

      {view.currentAnimals.length > 0 ? (
        <SectionCard
          title="Already on file"
          description="These are the animals we already have for your home — no need to enter them again."
        >
          <ul style={{ margin: 0, paddingLeft: 'var(--space-5)', color: 'var(--fg-muted)' }}>
            {view.currentAnimals.map((a, i) => (
              <li key={`${a.name}-${i}`} style={{ marginBottom: 'var(--space-1)' }}>
                {knownAnimalLine(a)}
              </li>
            ))}
          </ul>
        </SectionCard>
      ) : null}

      {saveError ? (
        <div className="alert" data-tone="danger" style={{ marginBottom: 'var(--space-5)' }}>
          <div className="alert-body">{saveError}</div>
        </div>
      ) : null}

      <SectionCard title={animals.length > 1 ? 'Your new animals' : 'Your new animal'}>
        {animals.map((a, i) => (
          <AnimalAddendumAnimalCard
            key={a.id}
            animal={a}
            index={i}
            total={animals.length}
            disabled={saving}
            uploading={uploading[a.id] === true}
            photoError={photoErrors[a.id] ?? null}
            onUpdate={updateAnimal}
            onRemove={removeAnimal}
            onPickPhoto={(id, file) => void pickPhoto(id, file)}
            onClearPhoto={clearPhoto}
          />
        ))}
        <button
          type="button"
          className="btn"
          data-variant="secondary"
          data-size="sm"
          disabled={saving}
          onClick={addAnimal}
        >
          + Add another animal
        </button>
      </SectionCard>

      {/* The money, in the server's own words so this page, the emails and the
          addendum are never a dollar apart. */}
      <AnimalAddendumTerms
        terms={terms}
        covered={view.coveredAnimals ?? []}
      />

      <div
        style={{
          display: 'flex',
          justifyContent: 'flex-end',
          alignItems: 'center',
          gap: 'var(--space-3)',
          marginTop: 'var(--space-6)',
          flexWrap: 'wrap',
        }}
      >
        {!canSubmit && reasons[0] ? (
          <span className="helper" style={{ color: 'var(--fg-muted)' }}>
            {reasons[0]}
          </span>
        ) : null}
        <button
          type="button"
          className="btn"
          data-size="lg"
          disabled={saving || !canSubmit}
          onClick={() => void handleSubmit()}
        >
          {saving ? 'Submitting…' : 'Submit'}
        </button>
      </div>
    </>,
  );
}
